import * as THREE from 'three';
import {
  addSelectedObject,
  deselectAll,
  removeSelectedObject,
} from '../stores/selectionStore';
import { SceneObject, SceneObjectSchema } from './sceneObject';
import { stringSchema } from '@/data/common';
import {
  type MechanicType,
  UNKNOWN_MECHANIC_CONFIG,
  getMechanicConfig,
} from '@/data/mechanic';
import z from 'zod';

export const SelectableObjectSchema = SceneObjectSchema.extend({
  name: stringSchema.default('Object'),
  extra: z
    .object({
      additional: z.unknown().default([]),
      saveAdditional: z.boolean().default(false),
    })
    .default({ additional: [], saveAdditional: false }),
});

export abstract class SelectableObject<
  SchemaType extends typeof SelectableObjectSchema =
    typeof SelectableObjectSchema,
> extends SceneObject<SchemaType> {
  static override schema = SelectableObjectSchema;
  private selected = false;

  constructor(options: z.input<typeof SelectableObjectSchema>) {
    super(options);
  }

  abstract override getPickTargets(): THREE.Object3D[];

  abstract getAabb(): THREE.Box3;

  get isSelected() {
    return this.selected;
  }

  override onClick(
    _intersection: THREE.Intersection,
    event: PointerEvent,
  ): void {
    if (!event.shiftKey) {
      deselectAll();
    }
    this.select();
  }

  toggleSelection() {
    if (this.selected) {
      this.deselect();
    } else {
      this.select();
    }
  }

  select() {
    if (this.selected) {
      return;
    }

    this.selected = true;
    addSelectedObject(this);
    this.onSelectionChanged(true);
  }

  deselect() {
    if (!this.selected) {
      return;
    }

    this.selected = false;
    removeSelectedObject(this);
    this.onSelectionChanged(false);
  }

  override dispose() {
    this.deselect();
    super.dispose();
  }

  getWorldAabb(target = new THREE.Box3()) {
    this.outerObject.updateMatrixWorld(true);
    return target
      .copy(this.getAabb().translate(this.data.offset))
      .applyMatrix4(this.outerObject.matrixWorld);
  }

  protected onSelectionChanged(_selected: boolean): void {}

  encode(): MechanicType {
    const mechanicConfig =
      getMechanicConfig(this.data.name) || UNKNOWN_MECHANIC_CONFIG;
    return mechanicConfig.encode(this.getData());
  }
}
